import {
    BarcodeFormat,
    BinaryBitmap,
    DecodeHintType,
    HTMLCanvasElementLuminanceSource,
    HybridBinarizer,
    MultiFormatReader,
} from "@zxing/library";
import { useCallback, useEffect, useRef } from "react";
import { equalizeHistogram } from "../../scenes/image-utils";

export const useBarcodeScanner = ({
    webcamRef,
    canvasRef,
    onScan,
    filter,
    debugMode,
    setIsLandscape,
}: any) => {
    const hints = useRef(new Map());
    const reader = useRef(new MultiFormatReader());
    const timerRef = useRef<any | null>(null);
    const isScanningRef = useRef(false);
    const beepAudioRef = useRef<HTMLAudioElement | null>(null);
    const lastEmittedTextRef = useRef<{ text: string | null; at: number }>({ text: null, at: 0 });

    useEffect(() => {
        const formats = [
            BarcodeFormat.AZTEC,
            BarcodeFormat.CODABAR,
            BarcodeFormat.CODE_39,
            BarcodeFormat.CODE_93,
            BarcodeFormat.CODE_128,
            BarcodeFormat.DATA_MATRIX,
            BarcodeFormat.ITF,
            BarcodeFormat.MAXICODE,
            BarcodeFormat.PDF_417,
            BarcodeFormat.QR_CODE,
            BarcodeFormat.RSS_14,
            BarcodeFormat.RSS_EXPANDED,
        ];
        hints.current.set(DecodeHintType.POSSIBLE_FORMATS, formats);
        hints.current.set(DecodeHintType.CHARACTER_SET, "UTF-8");
        hints.current.set(DecodeHintType.TRY_HARDER, true);
        reader.current.setHints(hints.current);
    }, []);

    const playBeep = useCallback(() => {
        if (!beepAudioRef.current) {
            beepAudioRef.current = new Audio("/sound/beep.mp3");
        }
        beepAudioRef.current.play().catch(() => null);
    }, []);

    const handleScan = useCallback(
        (decodedText: string) => {
            const filteredValue = typeof filter === "function" ? filter(decodedText) : decodedText;
            if (filteredValue) {
                // Throttle identical scans to avoid emitting duplicates when the same code is in view.
                const now = Date.now();
                if ((lastEmittedTextRef.current.text === filteredValue) && (now - lastEmittedTextRef.current.at < 1500)) {
                    return;
                }

                lastEmittedTextRef.current = { text: filteredValue, at: now };

                onScan(filteredValue);
            }
        },
        [onScan, filter]
    );

    const captureAndScan = useCallback(async () => {
        if (!webcamRef.current || isScanningRef.current) return;

        const imageSrc = webcamRef.current.getScreenshot();
        if (!imageSrc || !canvasRef.current) return;

        isScanningRef.current = true;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        const img = new Image();
        img.src = imageSrc;

        img.onload = () => {
            let didEmitScan = false;

            try {
                const videoWidth = img.width;
                const videoHeight = img.height;
                setIsLandscape(videoWidth > videoHeight);

                let scanAreaWidth;
                let scanAreaHeight;

                if (videoWidth > videoHeight) {
                    scanAreaWidth = videoWidth * 0.6;
                    scanAreaHeight = videoHeight * 0.4;
                } else {
                    scanAreaWidth = videoWidth * 0.8;
                    scanAreaHeight = videoHeight * 0.3;
                }

                const scanAreaLeft = (videoWidth - scanAreaWidth) / 2;
                const scanAreaTop = (videoHeight - scanAreaHeight) / 2;

                canvas.width = scanAreaWidth;
                canvas.height = scanAreaHeight;

                ctx?.drawImage(
                    img,
                    scanAreaLeft,
                    scanAreaTop,
                    scanAreaWidth,
                    scanAreaHeight,
                    0,
                    0,
                    scanAreaWidth,
                    scanAreaHeight
                );

                const luminanceSource = new HTMLCanvasElementLuminanceSource(canvas);
                const binaryBitmap = new BinaryBitmap(new HybridBinarizer(luminanceSource));

                try {
                    const result = reader.current.decode(binaryBitmap);
                    const resultText = result.getText();
                    playBeep();
                    handleScan(resultText);
                    didEmitScan = true;
                } catch {
                    // eslint-disable-next-line no-console
                    console.log("No barcode detected");
                }

                if (debugMode && !didEmitScan) {
                    const fullImageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
                    if (fullImageData) {
                        const equalizedImageData = equalizeHistogram(fullImageData);
                        ctx?.clearRect(0, 0, canvas.width, canvas.height);
                        ctx?.putImageData(equalizedImageData, 0, 0);

                        const luminanceSourceDebug = new HTMLCanvasElementLuminanceSource(canvas);
                        const binaryBitmapDebug = new BinaryBitmap(new HybridBinarizer(luminanceSourceDebug));

                        try {
                            const resultDebug = reader.current.decode(binaryBitmapDebug);
                            const resultTextDebug = resultDebug.getText();
                            playBeep();
                            handleScan(resultTextDebug);
                        } catch {
                            // eslint-disable-next-line no-console
                            console.log("No barcode detected in debug mode");
                        }
                    }
                }
            } finally {
                isScanningRef.current = false;
            }
        };
    }, [handleScan, playBeep, webcamRef, canvasRef, debugMode, setIsLandscape]);

    useEffect(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(async () => {
            await captureAndScan();
        }, 300);
        return () => clearInterval(timerRef.current);
    }, [captureAndScan]);
};
